import { createContext, useContext, useLayoutEffect, useMemo } from 'react';

interface AppLoaderHost {
  acquire: () => () => void;
  settle: () => void;
  dispose: () => void;
}

export const AppLoaderContext = createContext<AppLoaderHost | null>(null);

export function useAppLoaderHost(element: HTMLElement | null): AppLoaderHost | null {
  return useMemo(() => {
    if (!element) {
      return null;
    }

    const owners = new Set<symbol>();
    let generation = 0;
    let disposed = false;
    const hide = () => {
      element.style.visibility = 'hidden';
      element.setAttribute('aria-hidden', 'true');
    };
    const settle = () => {
      const pendingGeneration = ++generation;
      // React cleans up one fallback before mounting its replacement in the same
      // commit. Settle after that handoff, before the browser's next paint.
      queueMicrotask(() => {
        if (!disposed && generation === pendingGeneration && owners.size === 0) {
          hide();
        }
      });
    };

    return {
      acquire: () => {
        disposed = false;
        const owner = Symbol();
        owners.add(owner);
        generation++;
        element.style.visibility = 'visible';
        element.removeAttribute('aria-hidden');
        return () => {
          owners.delete(owner);
          settle();
        };
      },
      settle,
      dispose: () => {
        disposed = true;
        generation++;
        hide();
      },
    };
  }, [element]);
}

export function useFullScreenLoader(): boolean {
  const host = useContext(AppLoaderContext);
  useLayoutEffect(() => host?.acquire(), [host]);
  return host !== null;
}
